import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';

import {
  Container,
  ContentContainer,
  PageTitle,
  StyledTextField,
  palette,
} from '@src/core/styles';

import {
  CheckBoxWrapper,
  FieldGroup,
  FieldGroupWrapper,
  InputGroup,
} from '../login-page/login-page.styles';
import { FieldGroupRow } from '../registration-page/registration-page.styles';
import { SettingsFormValues } from '@src/core/types';
import { settingsSchema } from '@src/core/schemas';
import { ReactComponent as Check } from '@src/core/icons/check.svg';
import { Button, CheckBox } from '@src/components';
import { ApiTokensPanel } from './api-tokens-panel';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Authentication, Settings } from '@src/core/services';
import { enqueueSnackbar } from 'notistack';
import { FormContainer } from '../route-management-page/components/form-component/form-components.styles';
import type {
  SettingsErrors,
  SettingsErrorResponse,
} from '@src/core/types/settings';
import { AxiosError } from 'axios';

export const SettingsComponent: FC = () => {
  const [checked, setChecked] = useState<boolean>(false);
  const [errorsRequest, setErrorsRequest] = useState<SettingsErrors>();

  const { data: info, isLoading } = useQuery({
    queryKey: ['info'],
    queryFn: () => Authentication.info(),
  });

  const inititalValues: SettingsFormValues | undefined = useMemo(() => {
    if (isLoading) {
      return undefined;
    }
    if (!isLoading) {
      if (info?.data) {
        const userInfo = info?.data;
        return {
          first_name: userInfo.first_name,
          last_name: userInfo.last_name,
          old_password: '',
          password: '',
          password2: '',
        };
      } else {
        return undefined;
      }
    }
  }, [info?.data, isLoading]);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<SettingsFormValues>({
    mode: 'onChange',
    resolver: joiResolver(settingsSchema),
    defaultValues: inititalValues,
  });

  const typePasswordField = checked ? 'text' : 'password';

  const { mutate } = useMutation(Settings.update, {
    onSuccess: (response) => {
      enqueueSnackbar('Настройки изменены', {
        variant: 'reportComplete',
        className: 'success',
        preventDuplicate: true,
      });
    },
    onError: (error: AxiosError<SettingsErrorResponse>) => {
      setErrorsRequest(error.response?.data.errors);
    },
  });

  const changeHandler = () => {
    setChecked(!checked);
  };

  const onSubmit = (data: SettingsFormValues) => {
    mutate(data);
  };

  const tariff = info?.data.tariff;
  const apiUrl = info?.data.url;

  const handleCopyUrl = async () => {
    if (!apiUrl) return;
    try {
      await navigator.clipboard.writeText(apiUrl);
      enqueueSnackbar('URL скопирован', {
        variant: 'reportComplete',
        className: 'success',
        preventDuplicate: true,
      });
    } catch {
      enqueueSnackbar('Ошибка при копировании', {
        variant: 'reportComplete',
        className: 'error',
        preventDuplicate: true,
      });
    }
  };

  return (
    <Container>
      <ContentContainer>
        <FormContainer>
          <PageTitle>Настройки</PageTitle>

          {info?.data && (
            <div style={{
              display: 'flex',
              gap: '24px',
              flexWrap: 'wrap',
              marginBottom: '32px',
            }}>
              <div style={{
                flex: 1,
                minWidth: '280px',
                padding: '20px',
                borderRadius: '12px',
                background: '#f8f9fa',
                border: '1px solid #e0e0e0',
              }}>
                <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Ваш API домен
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <code style={{ fontSize: '14px', fontWeight: 600, color: '#1976d2', wordBreak: 'break-all' }}>
                    {apiUrl}
                  </code>
                  <span
                    onClick={handleCopyUrl}
                    style={{ cursor: 'pointer', fontSize: '12px', color: '#666', textDecoration: 'underline' }}
                  >
                    копировать
                  </span>
                </div>
              </div>
              {tariff && (
                <div style={{
                  flex: 1,
                  minWidth: '280px',
                  padding: '20px',
                  borderRadius: '12px',
                  background: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                }}>
                  <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    Тариф
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
                    {tariff.name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {tariff.api_count} API методов • {tariff.request_count} запросов/мин
                  </div>
                </div>
              )}
            </div>
          )}

          <ApiTokensPanel />

          <form onSubmit={handleSubmit(onSubmit)}>
            <FieldGroupWrapper>
              <FieldGroup>
                <FieldGroupRow gap={24}>
                  <Controller
                    control={control}
                    name="first_name"
                    render={({ field: { value, onBlur, onChange } }) => (
                      <StyledTextField
                        onBlur={onBlur}
                        variant="outlined"
                        label="Имя"
                        autoComplete="on"
                        InputLabelProps={{
                          sx: {
                            '&.Mui-focused': {
                              color: `${palette.colors.green_base}`,
                            },
                            '&.Mui-error': {
                              color: `${palette.colors.error}`,
                            },
                          },
                        }}
                        value={value}
                        error={
                          !!errors?.first_name?.message ||
                          !!errorsRequest?.first_name
                        }
                        helperText={
                          errors?.first_name?.message ||
                          errorsRequest?.first_name
                        }
                        onChange={onChange}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="last_name"
                    render={({ field: { value, onBlur, onChange } }) => (
                      <StyledTextField
                        onBlur={onBlur}
                        variant="outlined"
                        label="Фамилия"
                        autoComplete="on"
                        InputLabelProps={{
                          sx: {
                            '&.Mui-focused': {
                              color: `${palette.colors.green_base}`,
                            },
                            '&.Mui-error': {
                              color: `${palette.colors.error}`,
                            },
                          },
                        }}
                        value={value}
                        error={
                          !!errors?.last_name?.message ||
                          !!errorsRequest?.last_name
                        }
                        helperText={
                          errors?.last_name?.message || errorsRequest?.last_name
                        }
                        onChange={onChange}
                      />
                    )}
                  />
                </FieldGroupRow>
                <Controller
                  control={control}
                  name="old_password"
                  render={({ field: { value, onBlur, onChange } }) => (
                    <StyledTextField
                      onBlur={onBlur}
                      variant="outlined"
                      type="password"
                      label="Пароль"
                      InputLabelProps={{
                        sx: {
                          '&.Mui-focused': {
                            color: `${palette.colors.green_base}`,
                          },
                          '&.Mui-error': { color: `${palette.colors.error}` },
                        },
                      }}
                      autoComplete="off"
                      value={value}
                      error={
                        !!errors?.old_password?.message ||
                        !!errorsRequest?.old_password
                      }
                      helperText={
                        errors?.old_password?.message ||
                        errorsRequest?.old_password
                      }
                      onChange={onChange}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="password"
                  render={({ field: { value, onBlur, onChange } }) => (
                    <StyledTextField
                      onBlur={onBlur}
                      variant="outlined"
                      type="password"
                      label="Новый пароль"
                      InputLabelProps={{
                        sx: {
                          '&.Mui-focused': {
                            color: `${palette.colors.green_base}`,
                          },
                          '&.Mui-error': { color: `${palette.colors.error}` },
                        },
                      }}
                      autoComplete="off"
                      value={value}
                      error={
                        !!errors?.password?.message || !!errorsRequest?.password
                      }
                      helperText={
                        errors?.password?.message || errorsRequest?.password
                      }
                      onChange={onChange}
                    />
                  )}
                />
                <div>
                  <InputGroup>
                    <Controller
                      control={control}
                      name="password2"
                      render={({ field: { value, onBlur, onChange } }) => (
                        <StyledTextField
                          onBlur={onBlur}
                          variant="outlined"
                          type={typePasswordField}
                          label="Подтверждение нового пароля"
                          InputLabelProps={{
                            sx: {
                              '&.Mui-focused': {
                                color: `${palette.colors.green_base}`,
                              },
                              '&.Mui-error': {
                                color: `${palette.colors.error}`,
                              },
                            },
                          }}
                          autoComplete="off"
                          value={value}
                          error={
                            !!errors?.password2?.message ||
                            !!errorsRequest?.password2
                          }
                          helperText={
                            errors?.password2?.message ||
                            errorsRequest?.password2
                          }
                          onChange={onChange}
                        />
                      )}
                    />
                  </InputGroup>
                  <CheckBoxWrapper>
                    <CheckBox
                      label="Показать пароль"
                      checked={checked}
                      onChange={changeHandler}
                    />
                  </CheckBoxWrapper>
                  <Button
                    text="Сохранить"
                    type="submit"
                    icon={<Check />}
                    borderradius="L"
                    disabled={!isValid}
                  />
                </div>
              </FieldGroup>
            </FieldGroupWrapper>
          </form>
        </FormContainer>
      </ContentContainer>
    </Container>
  );
};
